import { useEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import Feedback from "../components/Feedback";
import { CheckIcon } from "../components/Icons";
import ui from "../components/ui.module.css";
import { isGeometryRight } from "../data/activityAdapter";
import {
  clampTo,
  placeOn,
  placedFrom,
  responseOf,
  snapTo,
  targetsOf,
} from "../data/maths/coordinates";

const CELEBRATE_MS = 700;
const MAX_TICK_LABELS = 20;

const number = (value) => String(value).replace(/^-/, "−");
export const coordinateText = ({ x, y }) => `(${number(x)}, ${number(y)})`;

const valuesAlong = ({ min, max, step }) => {
  const values = [];
  const count = Math.round((max - min) / step);
  for (let i = 0; i <= count; i += 1) values.push(Number((min + i * step).toFixed(10)));
  return values;
};

// The drawing area in plane units: y is flipped so up is positive, with a
// margin for labels. Everything below is drawn in these units, so the plane
// scales to any screen and a tap converts back without knowing pixels.
const viewOf = (plane) => {
  const width = plane.x.max - plane.x.min;
  const height = plane.y.max - plane.y.min;
  const unit = Math.max(width, height);
  const margin = unit * 0.08;
  return {
    minX: plane.x.min - margin,
    minY: -plane.y.max - margin,
    width: width + margin * 2,
    height: height + margin * 2,
    unit,
  };
};

const Plane = ({ plane, initial, drawn, highlight, interactive, onPlace, onRelease, label }) => {
  const svgRef = useRef(null);
  const dragging = useRef(false);
  const view = viewOf(plane);
  const font = view.unit / 26;
  const radius = view.unit / 45;

  // Client coordinates → plane coordinates, for the "meet" scaling the SVG
  // uses. Only the result is ever kept.
  const toPlane = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    const scale = Math.min(rect.width / view.width, rect.height / view.height) || 1;
    const offsetX = (rect.width - view.width * scale) / 2;
    const offsetY = (rect.height - view.height * scale) / 2;
    return {
      x: view.minX + (event.clientX - rect.left - offsetX) / scale,
      y: -(view.minY + (event.clientY - rect.top - offsetY) / scale),
    };
  };

  const down = (event) => {
    if (!interactive || (event.pointerType === "mouse" && event.button !== 0)) return;
    event.preventDefault();
    svgRef.current.setPointerCapture?.(event.pointerId);
    dragging.current = true;
    onPlace(toPlane(event));
  };
  const move = (event) => {
    if (dragging.current) onPlace(toPlane(event));
  };
  const up = () => {
    if (!dragging.current) return;
    dragging.current = false;
    onRelease();
  };

  const xs = valuesAlong(plane.x);
  const ys = valuesAlong(plane.y);
  const every = (values) => Math.max(1, Math.ceil(values.length / MAX_TICK_LABELS));
  const xAxisAt = clampTo(0, plane.y);
  const yAxisAt = clampTo(0, plane.x);
  const line = (props) => <line vectorEffect="non-scaling-stroke" {...props} />;
  const pathOf = (points, closed) =>
    points.map((p, i) => `${i ? "L" : "M"}${p.x} ${-p.y}`).join(" ") + (closed ? " Z" : "");

  return (
    <svg
      ref={svgRef}
      className={`${ui.plane} ${interactive ? ui.planeActive : ""}`}
      viewBox={`${view.minX} ${view.minY} ${view.width} ${view.height}`}
      role="img"
      aria-label={label}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <rect x={plane.x.min} y={-plane.y.max} width={plane.x.max - plane.x.min} height={plane.y.max - plane.y.min} className={ui.planePaper} />
      {plane.showGrid && (
        <g className={ui.planeGrid}>
          {xs.map((x) => line({ key: `gx${x}`, x1: x, x2: x, y1: -plane.y.max, y2: -plane.y.min }))}
          {ys.map((y) => line({ key: `gy${y}`, y1: -y, y2: -y, x1: plane.x.min, x2: plane.x.max }))}
        </g>
      )}
      {plane.showAxes && (
        <g className={ui.planeAxes}>
          {line({ x1: plane.x.min, x2: plane.x.max, y1: -xAxisAt, y2: -xAxisAt })}
          {line({ x1: yAxisAt, x2: yAxisAt, y1: -plane.y.min, y2: -plane.y.max })}
        </g>
      )}
      {plane.showTickLabels && (
        <g className={ui.planeLabels} fontSize={font}>
          {xs.filter((_, i) => i % every(xs) === 0).map((x) =>
            x === yAxisAt && plane.showAxes ? null : (
              <text key={`tx${x}`} x={x} y={-xAxisAt + font * 1.2} textAnchor="middle">
                {number(x)}
              </text>
            )
          )}
          {ys.filter((_, i) => i % every(ys) === 0).map((y) =>
            y === xAxisAt && plane.showAxes ? null : (
              <text key={`ty${y}`} x={yAxisAt - font * 0.4} y={-y + font * 0.35} textAnchor="end">
                {number(y)}
              </text>
            )
          )}
          {plane.showAxes && (
            <text x={yAxisAt - font * 0.4} y={-xAxisAt + font * 1.2} textAnchor="end">
              0
            </text>
          )}
        </g>
      )}
      {plane.x.label && (
        <text className={ui.planeAxisLabel} fontSize={font} x={plane.x.max} y={-xAxisAt - font * 0.5} textAnchor="end">
          {plane.x.label}
        </text>
      )}
      {plane.y.label && (
        <text className={ui.planeAxisLabel} fontSize={font} x={yAxisAt + font * 0.5} y={-plane.y.max + font} textAnchor="start">
          {plane.y.label}
        </text>
      )}

      {initial.figures.map((figure) => (
        <path key={`if-${figure.id}`} className={ui.planeGiven} vectorEffect="non-scaling-stroke" d={pathOf(figure.vertices, figure.kind === "polygon")} />
      ))}
      {[...initial.points, ...initial.figures.flatMap((f) => f.vertices)].map((point, i) => (
        <g key={`ip-${i}`} className={ui.planeGivenPoint}>
          <circle cx={point.x} cy={-point.y} r={radius * 0.8} />
          {point.label && (
            <text x={point.x + radius * 1.2} y={-point.y - radius * 1.2} fontSize={font}>
              {point.label}
            </text>
          )}
        </g>
      ))}

      {drawn.figures.map((figure) => (
        <path key={`df-${figure.id}`} className={ui.planeFigure} vectorEffect="non-scaling-stroke" d={pathOf(figure.points, figure.closed)} />
      ))}
      {drawn.points.map((point) => (
        <g key={point.key} className={ui.planePoint}>
          {highlight === point.key && <circle className={ui.planeActiveRing} cx={point.x} cy={-point.y} r={radius * 1.9} />}
          <circle cx={point.x} cy={-point.y} r={radius} />
          <text x={point.x + radius * 1.3} y={-point.y - radius * 1.3} fontSize={font}>
            {point.name}
          </text>
        </g>
      ))}
    </svg>
  );
};

// A coordinate set with − and + rather than typing or fine dragging.
const Stepper = ({ axis, value, onChange, plane, disabled, name }) => {
  const range = plane[axis];
  const start = value ?? clampTo(0, range);
  const change = (by) => onChange(clampTo(snapTo(start + by, plane.snap), range));
  return (
    <div className={ui.stepper} role="group" aria-label={`${name}: ${axis}`}>
      <span className={ui.stepperAxis}>{axis}</span>
      <button type="button" className={ui.stepperButton} aria-label={`${axis} less`} onClick={() => change(value === null ? 0 : -plane.snap)} disabled={disabled}>
        {"−"}
      </button>
      <output className={ui.stepperValue} aria-live="polite">
        {value === null ? "?" : number(value)}
      </output>
      <button type="button" className={ui.stepperButton} aria-label={`${axis} more`} onClick={() => change(value === null ? 0 : plane.snap)} disabled={disabled}>
        +
      </button>
    </div>
  );
};

// Plot, read or transform points on a Cartesian plane (contract §5). Every
// target has a stable id; the learner's work is mathematical coordinates,
// snapped to the authored step and compared by id, never by pixel.
const CoordinatePlaneStep = ({ step, saved, onDone, saving }) => {
  const { plane, expected } = step;
  const reading = step.mode === "read_point";
  const targets = useMemo(() => targetsOf(expected), [expected]);
  // Learner-facing names: authored labels, or a plain ordinal.
  const names = useMemo(() => {
    const result = {};
    let pointCount = 0;
    for (const target of targets) {
      if (target.figure) {
        const index = target.figure.vertices.indexOf(target.point) + 1;
        result[target.key] = target.point.label || `${target.figure.label || "Shape"} corner ${index}`;
      } else {
        pointCount += 1;
        result[target.key] = target.point.label || `Point ${pointCount}`;
      }
    }
    return result;
  }, [targets]);

  const [placed, setPlaced] = useState(() => placedFrom(expected, saved));
  const firstOpen = (current) => targets.find((t) => !current[t.key])?.key || null;
  const [active, setActive] = useState(() => firstOpen(placedFrom(expected, saved)) || targets[0].key);
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const locked = saving || result === "right";

  const update = (next) => {
    setPlaced(next);
    setProblem(null);
    setResult(null);
  };

  const place = ({ x, y }) => {
    if (locked || !active) return;
    update({ ...placed, [active]: placeOn(plane, x, y) });
  };

  // After a tap, the next point without a place becomes the one to place.
  const advance = () => {
    const next = targets.find((t) => t.key !== active && !placed[t.key]);
    if (next) setActive(next.key);
  };

  const nudge = (dx, dy) => {
    const current = placed[active] || { x: clampTo(0, plane.x), y: clampTo(0, plane.y) };
    place({ x: current.x + dx * plane.snap, y: current.y + dy * plane.snap });
  };

  const setAxis = (key, axis, value) => {
    const current = placed[key] || {};
    update({ ...placed, [key]: { x: current.x ?? null, y: current.y ?? null, [axis]: value } });
  };

  const complete = (point) => Number.isFinite(point?.x) && Number.isFinite(point?.y);

  const check = () => {
    if (locked) return;
    if (!targets.every((t) => complete(placed[t.key]))) {
      setProblem(reading ? "Give both numbers for every point." : "Place every point first.");
      return;
    }
    const response = responseOf(expected, placed);
    if (!isGeometryRight(step, response)) {
      setResult("wrong");
      return;
    }
    setResult("right");
    timer.current = setTimeout(() => onDone(response), CELEBRATE_MS);
  };

  const drawnPoints = targets
    .filter((t) => complete(placed[t.key]))
    .map((t) => ({ key: t.key, name: names[t.key], ...placed[t.key] }));
  const drawnFigures = reading
    ? []
    : expected.figures.map((figure) => {
        const points = figure.vertices
          .map((vertex) => placed[`figure:${figure.id}:${vertex.id}`])
          .filter(complete);
        return {
          id: figure.id,
          points,
          closed: figure.kind === "polygon" && points.length === figure.vertices.length,
        };
      });

  return (
    <>
      <Prompt step={step} />
      <div className={ui.coordinate}>
        <Plane
          plane={plane}
          initial={step.initial}
          drawn={{ points: reading ? [] : drawnPoints, figures: drawnFigures }}
          highlight={locked ? null : active}
          interactive={!reading && !locked}
          onPlace={place}
          onRelease={advance}
          label={reading ? "Coordinate plane with points to read" : "Coordinate plane: tap to place a point"}
        />
        <div className={ui.coordinateSide}>
          {reading ? (
            <ul className={ui.coordinateTargets}>
              {targets.map((t) => (
                <li key={t.key} className={ui.readTarget}>
                  <span className={ui.targetName}>{names[t.key]}</span>
                  <Stepper axis="x" name={names[t.key]} plane={plane} value={placed[t.key]?.x ?? null} onChange={(v) => setAxis(t.key, "x", v)} disabled={locked} />
                  <Stepper axis="y" name={names[t.key]} plane={plane} value={placed[t.key]?.y ?? null} onChange={(v) => setAxis(t.key, "y", v)} disabled={locked} />
                </li>
              ))}
            </ul>
          ) : (
            <>
              <div className={ui.coordinateTargets} role="radiogroup" aria-label="Point to place">
                {targets.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="radio"
                    aria-checked={active === t.key}
                    className={`${ui.targetChip} ${active === t.key ? ui.targetChipOn : ""}`}
                    onClick={() => setActive(t.key)}
                    disabled={locked}
                  >
                    <span className={ui.targetName}>{names[t.key]}</span>
                    <span className={ui.targetAt}>{complete(placed[t.key]) ? coordinateText(placed[t.key]) : "not placed"}</span>
                  </button>
                ))}
              </div>
              <div className={ui.nudges} role="group" aria-label={`Move ${names[active] || "point"}`}>
                <button type="button" className={ui.stepperButton} aria-label="Left" onClick={() => nudge(-1, 0)} disabled={locked}>{"←"}</button>
                <button type="button" className={ui.stepperButton} aria-label="Up" onClick={() => nudge(0, 1)} disabled={locked}>{"↑"}</button>
                <button type="button" className={ui.stepperButton} aria-label="Down" onClick={() => nudge(0, -1)} disabled={locked}>{"↓"}</button>
                <button type="button" className={ui.stepperButton} aria-label="Right" onClick={() => nudge(1, 0)} disabled={locked}>{"→"}</button>
              </div>
            </>
          )}
          <p className={ui.mathProblem} role="alert">{problem || ""}</p>
          <div className={ui.toolButtons}>
            <button type="button" className={`${ui.button} ${ui.large} ${ui.secondary}`} onClick={() => { update({}); setActive(targets[0].key); }} disabled={locked}>
              Clear
            </button>
            <button type="button" className={`${ui.button} ${ui.large} ${ui.primary}`} onClick={check} disabled={locked}>
              <CheckIcon size={24} />
              {saving ? "Saving…" : "Check"}
            </button>
          </div>
          <Feedback kind={result} />
        </div>
      </div>
    </>
  );
};

export default CoordinatePlaneStep;
